import { useState, useMemo, useEffect } from "react";
import { Search, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  icon: string;
  faqs: FAQ[];
}

export default function FAQ() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [faqData, setFaqData] = useState<FAQSection[]>([]);
  const [faqConfig, setFaqConfig] = useState<any[]>([]); // State for FAQ-specific config fields
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFaqData = async () => {
      try {
        setLoading(true);
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/faq.json?t=${new Date().getTime()}`;
        const response = await fetch(url);
        const configdata = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`;
        const configResponse = await fetch(configdata);
        
        if (!response.ok) {
          throw new Error('Failed to load FAQ data');
        }
        if (!configResponse.ok) {
          throw new Error('Failed to load configuration data');
        }

        const data = await response.json();
        const configData = await configResponse.json();
        
        // Find "FAQ" page configuration
        const faqPageConfig = configData.find((page: any) => page.name.toLowerCase().includes("faq"));
        
        // Extract the nested fields from "faq_settings"
        const faqSettingsFields = faqPageConfig?.configurations?.find(
          (c: any) => c.key === "faq_settings"
        )?.fields || [];

        setFaqData(data);
        setFaqConfig(faqSettingsFields); // Store FAQ settings in state

      } catch (err) {
        setError('Failed to load FAQ content. Please try again later.');
        console.error('Error loading FAQ data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFaqData();
  }, []);

  // Helper function to check if a specific FAQ field is enabled
  const isFieldEnabled = (key: string) => {
    const fieldConfig = faqConfig.find((field: any) => field.key === key);
    // Return the default value if found, otherwise fallback to true to prevent accidental UI breakage 
    // (though you can change the fallback to false depending on your strictness preferences)
    if (!fieldConfig) return false;
    return fieldConfig.default === true || fieldConfig.default === "true";
  };

  // Handle "Expand First Item" configuration
  useEffect(() => {
    if (faqData.length > 0 && isFieldEnabled("expand_first_item_active") && expandedSections.length === 0) {
      setExpandedSections([faqData[0].title]);
    }
  }, [faqData, faqConfig]);

  const filteredData = useMemo(() => {
    if (!faqData.length || !searchQuery.trim()) {
      return faqData;
    }

    const keywords = searchQuery.toLowerCase().split(/\s+/).filter(k => k.length >= 2);

    if (keywords.length === 0) {
      return faqData;
    }

    const minMatchRatio = 0.7;
    const minMatches = Math.max(1, Math.ceil(keywords.length * minMatchRatio));

    return faqData
      .map(section => ({
        ...section,
        faqs: section.faqs.filter(faq => {
          const text = (faq.question + " " + faq.answer).toLowerCase();
          const matchCount = keywords.filter(keyword => text.includes(keyword)).length;
          return matchCount >= minMatches;
        })
      }))
      .filter(section => section.faqs.length > 0);
  }, [searchQuery, faqData]);

  const totalQuestions = faqData.reduce((sum, section) => sum + section.faqs.length, 0);
  const matchingQuestions = filteredData.reduce((sum, section) => sum + section.faqs.length, 0);

  // CHANGED LOGIC: This now only allows one section to be in the array at a time
  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionTitle)
        ? [] // If it's already open, close it
        : [sectionTitle] // If it's closed, set it as the ONLY open section
    );
  };

  return (
    <div id="faq" className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-white/20 rounded-full">
              <HelpCircle className="h-12 w-12" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-emerald-100 mb-8">
            Find answers to common questions about BillboardHub's advertising platform
          </p>

          {/* Configuration: Show/Hide Search Bar */}
          {isFieldEnabled("search_bar_active") && (
            <>
              <div className="max-w-2xl mx-auto relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg bg-white text-gray-900 border-0 rounded-xl shadow-lg focus:ring-2 focus:ring-emerald-300"
                />
              </div>

              {!loading && !error && (
                <p className="text-sm text-emerald-200 mt-4">
                  {searchQuery ? (
                    `Found ${matchingQuestions} matching questions`
                  ) : (
                    `Browse ${totalQuestions} questions across ${faqData.length} categories`
                  )}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {loading ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Loading FAQ content...</h3>
              <p className="text-gray-600">Please wait while we load the frequently asked questions.</p>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-red-400 mb-4">
                <HelpCircle className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading content</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Try Again
              </button>
            </CardContent>
          </Card>
        ) : filteredData.length === 0 && searchQuery.trim() ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-gray-400 mb-4">
                <Search className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600">
                Try adjusting your search terms or browse all categories below.
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Clear search
              </button>
            </CardContent>
          </Card>
        ) : faqData.length === 0 && !loading && !error ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-gray-400 mb-4">
                <HelpCircle className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQ content available</h3>
              <p className="text-gray-600">
                FAQ content is currently unavailable. Please check back later.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Configuration: Show/Hide Category Navigation */}
            {filteredData.map((section, sectionIndex) => {
              const isExpanded = expandedSections.includes(section.title) || searchQuery.length > 0;

              return (
                <Card key={sectionIndex} className="overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center justify-between p-6 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{section.icon}</span>
                      <div className="text-left">
                        <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                        <p className="text-sm text-gray-500">{section.faqs.length} questions</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <CardContent className="border-t bg-gray-50 p-0">
                      <Accordion type="single" collapsible className="w-full">
                        {section.faqs.map((faq, faqIndex) => (
                          <AccordionItem
                            key={faqIndex}
                            value={`${sectionIndex}-${faqIndex}`}
                            className="border-b last:border-b-0"
                          >
                            <AccordionTrigger className="px-6 py-4 hover:bg-white text-left">
                              <span className="font-medium text-gray-900">{faq.question}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-4">
                              <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="py-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Still have questions?</h3>
              <p className="text-gray-600 mb-4">
                Our support team is here to help you 24/7
              </p>
              <a
                href="mailto:support@billboardhub.com"
                className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Contact Support
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}